import { useState, useMemo, useContext, createContext } from 'react'
import Dialog from '@mui/material/Dialog'
import useCallbackDebounced from '../../../hooks/shared/useCallbackDebounced'
import CompanyPlanningRecordDetails from '../../features/companyPlanningRecords/CompanyPlanningRecordDetails'
import { useOverlayPageNavigateBack } from '../../../hooks/navigation/usePersistedBackgroundLocation'

const CompanyPlanningRecordDialogContext = createContext<{
  openCompanyPlanningRecordDialog: (args: { companyPlanningRecordId: string }) => void
  closeCompanyPlanningRecordDialog: () => void
} | null>(null)
const useCompanyPlanningRecordDialog = ({
  companyPlanningRecordId,
}: {
  companyPlanningRecordId: string
}) => {
  const context = useContext(CompanyPlanningRecordDialogContext)
  if (!context) {
    throw new Error(
      'no CompanyPlanningRecordDialogProvider found when calling useCompanyPlanningRecordDialog()',
    )
  }
  const { openCompanyPlanningRecordDialog, closeCompanyPlanningRecordDialog } = context
  const wrappedOpenCompanyPlanningRecordDialog = useCallbackDebounced(() => {
    openCompanyPlanningRecordDialog({ companyPlanningRecordId })
  }, [openCompanyPlanningRecordDialog, companyPlanningRecordId])
  return {
    openCompanyPlanningRecordDialog: wrappedOpenCompanyPlanningRecordDialog,
    closeCompanyPlanningRecordDialog,
  }
}
const CompanyPlanningRecordDialogProvider = ({ children }) => {
  const overlayPageNavigateBack = useOverlayPageNavigateBack()
  const [open, setOpen] = useState(false)
  const [companyPlanningRecordId, setCompanyPlanningRecordId] = useState<string>()
  const openCompanyPlanningRecordDialog = useCallbackDebounced(
    ({ companyPlanningRecordId }: { companyPlanningRecordId: string }) => {
      setCompanyPlanningRecordId(companyPlanningRecordId)
      setOpen(true)
    },
    [],
  )
  const closeCompanyPlanningRecordDialog = useCallbackDebounced(() => {
    setOpen(false)
  }, [])
  const onClose = useCallbackDebounced(() => {
    overlayPageNavigateBack()
  }, [overlayPageNavigateBack])
  const onExited = useCallbackDebounced(() => {
    setCompanyPlanningRecordId(undefined)
  }, [])
  const value = useMemo(
    () => ({
      openCompanyPlanningRecordDialog,
      closeCompanyPlanningRecordDialog,
    }),
    [closeCompanyPlanningRecordDialog, openCompanyPlanningRecordDialog],
  )
  return (
    <CompanyPlanningRecordDialogContext.Provider value={value}>
      {children}
      <Dialog
        fullScreen
        open={open}
        onClose={onClose}
        sx={{
          zIndex: 20,
        }}
        disableEscapeKeyDown
        slotProps={{
          transition: {
            onExited,
          },
        }}
      >
        {companyPlanningRecordId && (
          <CompanyPlanningRecordDetails
            companyPlanningRecordId={companyPlanningRecordId}
            onClose={onClose}
          />
        )}
      </Dialog>
    </CompanyPlanningRecordDialogContext.Provider>
  )
}

export { useCompanyPlanningRecordDialog, CompanyPlanningRecordDialogProvider }
export default CompanyPlanningRecordDialogProvider
